import { useMemo, useState } from 'react';
import { GEWERK, STATUS } from './gemeinsam';
import {
  FilterBar, FilterDrawer, type FilterDefinition, type FilterWerte,
} from '../../muster';

export interface Filter {
  loesungsart: string[];
  status: string[];
  befund: string[];
  gewerkWeicht: string;
  gewerkBleibt: string;
  korb: string;
  mindestDurchdringung: string;
  suche: string;
  nurMitAnweisung: boolean;
  nurAutomatisch: boolean;
  sortierung: string;
}

export const FILTER_LEER: Filter = {
  loesungsart: [], status: [], befund: [],
  gewerkWeicht: '', gewerkBleibt: '', korb: '',
  mindestDurchdringung: '', suche: '',
  nurMitAnweisung: false, nurAutomatisch: false,
  sortierung: 'durchdringung',
};

const BEFUNDE = ['machbar', 'eng', 'keine_loesung', 'nicht_geprueft'];

/**
 * Die Filterleiste des Planerarbeitsplatzes (Batch 7).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * SEIT U6a: `FilterBar` UND `FilterDrawer` STATT ELF FELDER IN EINER ZEILE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Vorher standen alle elf Filter nebeneinander, darunter drei
 * `<select multiple size={3}>`. Bei 1440 px schnitt die dritte Zeile der
 * Mehrfachlisten ab — „Planer gefragt" und „nicht geprueft" waren nicht mehr
 * lesbar (gemessen am Abzug vom 18.09.2026). Abschnitt 11 des Auftrags sagt
 * genau das: wenige Filter in die Leiste, viele ins Schubfach.
 *
 * Der ZUSTAND bleibt, wo er war: Koordination.tsx führt ihn und schreibt ihn
 * in die Adresszeile. Diese Komponente übersetzt nur zwischen `Filter` und
 * den `FilterWerte` der Musterbibliothek — sie ruft weiterhin kein `api.*`.
 */
export function Filterleiste({
  filter, onChange, loesungsarten, koerbe, gezeigt, gesamt,
}: {
  filter: Filter;
  onChange: (f: Filter) => void;
  loesungsarten: any[];
  koerbe: string[];
  gezeigt: number;
  gesamt: number;
}) {
  const [schubfach, setSchubfach] = useState(false);

  const definitionen: FilterDefinition[] = useMemo(() => {
    const d: FilterDefinition[] = [
      {
        schluessel: 'loesungsart', beschriftung: 'Lösungsart',
        art: 'mehrfachauswahl',
        optionen: (loesungsarten ?? []).map((l: any) => ({
          wert: l.schluessel as string, beschriftung: l.bezeichnung as string,
        })),
      },
      {
        schluessel: 'status', beschriftung: 'Status', art: 'mehrfachauswahl',
        optionen: Object.entries(STATUS)
          .filter(([k]) => k !== 'ohne_anweisung')
          .map(([k, v]) => ({ wert: k, beschriftung: v.text })),
      },
      {
        schluessel: 'befund', beschriftung: 'Befund', art: 'mehrfachauswahl',
        optionen: BEFUNDE.map((b) => ({
          wert: b, beschriftung: b.replace(/_/g, ' '),
        })),
      },
      {
        schluessel: 'gewerkWeicht', beschriftung: 'Gewerk weicht', art: 'auswahl',
        optionen: [
          { wert: '', beschriftung: 'alle' },
          ...Object.entries(GEWERK).map(([k, name]) => ({ wert: k, beschriftung: name })),
        ],
      },
      {
        schluessel: 'gewerkBleibt', beschriftung: 'Gewerk bleibt', art: 'auswahl',
        optionen: [
          { wert: '', beschriftung: 'alle' },
          ...Object.entries(GEWERK).map(([k, name]) => ({ wert: k, beschriftung: name })),
        ],
      },
    ];

    if (koerbe.length > 0) {
      d.push({
        schluessel: 'korb', beschriftung: 'Korb', art: 'auswahl',
        optionen: [
          { wert: '', beschriftung: 'alle' },
          ...koerbe.map((k) => ({ wert: k, beschriftung: k })),
        ],
      });
    }

    d.push(
      {
        schluessel: 'mindestDurchdringung', beschriftung: 'Mindest-Durchdringung',
        art: 'text', platzhalter: 'mm',
        hinweis: 'In Millimetern. Leer heißt: jede Durchdringung.',
      },
      { schluessel: 'nurMitAnweisung', beschriftung: 'nur mit Anweisung', art: 'haken' },
      { schluessel: 'nurAutomatisch', beschriftung: 'nur automatisch', art: 'haken' },
    );

    /* Die SORTIERUNG steht bewusst NICHT in dieser Liste: `FilterBar` zählt
       jeden gesetzten Wert als aktiven Filter, und „nach Durchdringung
       sortiert" ist keiner - die Leiste meldete sonst dauerhaft
       „1 Filter gesetzt", ohne dass etwas eingegrenzt wäre. Sie steht als
       eigenes Bedienelement rechts. */

    return d;
  }, [loesungsarten, koerbe]);

  /** `Filter` → `FilterWerte`. Die Suche führt `FilterBar` getrennt. */
  const werte: FilterWerte = {
    loesungsart: filter.loesungsart,
    status: filter.status,
    befund: filter.befund,
    gewerkWeicht: filter.gewerkWeicht,
    gewerkBleibt: filter.gewerkBleibt,
    korb: filter.korb,
    mindestDurchdringung: filter.mindestDurchdringung,
    nurMitAnweisung: filter.nurMitAnweisung,
    nurAutomatisch: filter.nurAutomatisch,
  };

  /** `FilterWerte` → `Filter`. Fehlende Werte fallen auf den leeren zurück. */
  const uebernehmen = (neu: FilterWerte) => {
    onChange({
      ...filter,
      loesungsart: (neu.loesungsart as string[]) ?? [],
      status: (neu.status as string[]) ?? [],
      befund: (neu.befund as string[]) ?? [],
      gewerkWeicht: (neu.gewerkWeicht as string) ?? '',
      gewerkBleibt: (neu.gewerkBleibt as string) ?? '',
      korb: (neu.korb as string) ?? '',
      mindestDurchdringung: (neu.mindestDurchdringung as string) ?? '',
      nurMitAnweisung: Boolean(neu.nurMitAnweisung),
      nurAutomatisch: Boolean(neu.nurAutomatisch),
    });
  };

  return (
    <>
      <FilterBar
        filter={definitionen}
        werte={werte}
        beiAenderung={uebernehmen}
        inLeiste={3}
        suche={filter.suche}
        beiSuche={(s) => onChange({ ...filter, suche: s })}
        suchePlatzhalter="ElementId, UniqueId, Achse, Systemname …"
        beiSchubfach={() => setSchubfach(true)}
      >
        <label style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center',
                        gap: 6 }}>
          Sortierung
          <select
            value={filter.sortierung}
            onChange={(e) => onChange({ ...filter, sortierung: e.target.value })}
          >
            <option value="durchdringung">Durchdringung ↓</option>
            <option value="betrag">Betrag</option>
            <option value="status">Status</option>
            <option value="gewerk">Gewerk</option>
            <option value="geaendert">zuletzt geändert</option>
          </select>
        </label>

        <span className="schwach" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
          {gezeigt} von {gesamt}
        </span>
      </FilterBar>

      <FilterDrawer
        offen={schubfach}
        filter={definitionen}
        werte={werte}
        beiAenderung={uebernehmen}
        beiSchliessen={() => setSchubfach(false)}
        beiZuruecksetzen={() => onChange(FILTER_LEER)}
        trefferText={`${gezeigt} von ${gesamt} Kollisionen`}
      />
    </>
  );
}
